import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import YouTubePlayer from '../YouTubePlayer';
import { attachHls, buildSources, clearPosition, observeOnScreen, prefetchFilm, readPosition, savePosition } from '../../utils/playerEngine';

const STALL_MS = 8000;

/**
 * FilmPlayer — one engine for every device.
 *  HLS (hls.js / native on iOS) → progressive MP4 → YouTube, switching automatically on fatal errors or stalls.
 *  The <video> element is stable across films so a user gesture that unlocks sound keeps working after source swaps.
 *  `lazy`: nothing is fetched until play() is called (poster only) — keeps the first paint light.
 *  Nothing attaches until the player is on screen; it pauses when scrolled away or the tab is hidden.
 *  Ref API: play({unmute}), pause, seekTo, setMuted, getCurrentTime, getDuration, getVideoElement, getEngine, setQuality
 */
const FilmPlayer = forwardRef(({
  film,
  muted = true,
  autoplay = false,
  lazy = false,
  fit = 'contain',
  resume = true,
  className = '',
  onPlay, onPause, onEnded, onTimeUpdate, onDurationChange, onBuffering, onEngineChange, onAutoplayBlocked, onExhausted, onQuality,
}, ref) => {
  const srcKey = film ? [film.id, film.hls_url, film.video_url, film.video_url_low, film.video_url_high, film.youtube_id].join('|') : '';
  const sources = useMemo(() => buildSources(film), [srcKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [srcState, setSrcState] = useState({ id: film?.id, idx: 0 });
  const srcIndex = srcState.id === film?.id ? srcState.idx : 0;
  const [armed, setArmed] = useState(autoplay || !lazy);
  const [attempt, setAttempt] = useState(0);
  const videoRef = useRef(null);
  const ytRef = useRef(null);
  const detachRef = useRef(null);
  const stallTimer = useRef(null);
  const resumeAt = useRef(0);
  const wantPlay = useRef(autoplay);
  const autoPaused = useRef(false);
  const [onScreen, setOnScreen] = useState(false);
  const [seen, setSeen] = useState(false);
  const durationRef = useRef(0);
  const hlsRef = useRef(null);
  const source = sources[srcIndex];
  const engine = source?.type || 'none';

  useEffect(() => {
    resumeAt.current = resume ? readPosition(film?.id) : 0;
    wantPlay.current = autoplay || wantPlay.current;
    if (autoplay) setArmed(true);
  }, [film?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (autoplay) { wantPlay.current = true; setArmed(true); } }, [autoplay]);
  useEffect(() => { onEngineChange?.(engine, source); }, [engine, source]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (armed && !source && film && (film.hls_url || film.video_url || film.video_url_low || film.video_url_high || film.youtube_id)) {
      // Deferred so the parent's film-change reset runs first and does not wipe this notice.
      const t = setTimeout(() => { onBuffering?.(false); onExhausted?.('unsupported'); }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [source, film, armed, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearStall = () => { if (stallTimer.current) { clearTimeout(stallTimer.current); stallTimer.current = null; } };

  const advance = useCallback((reason) => {
    clearStall();
    const v = videoRef.current;
    if (v && v.currentTime > 1) resumeAt.current = v.currentTime;
    if (srcIndex + 1 < sources.length) {
      console.warn(`[FilmPlayer] ${source?.type} failed (${reason}) → ${sources[srcIndex + 1].type}`);
      setSrcState({ id: film?.id, idx: srcIndex + 1 });
    } else {
      onBuffering?.(false);
      onExhausted?.(reason);
    }
  }, [srcIndex, sources, source, film?.id, onBuffering, onExhausted]);

  const armStall = useCallback(() => {
    clearStall();
    stallTimer.current = setTimeout(() => {
      const v = videoRef.current;
      if (v && v.paused && !wantPlay.current) return;
      advance('stall');
    }, STALL_MS);
  }, [advance]);

  const tryPlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch((err) => {
      if (err?.name === 'NotAllowedError' && !v.muted) {
        v.muted = true;
        onAutoplayBlocked?.();
        v.play().catch(() => {});
      }
    });
  }, [onAutoplayBlocked]);

  // Attach the current source to the stable <video>
  useEffect(() => {
    const v = videoRef.current;
    if (!armed || !seen || !source || source.type === 'youtube' || !v) return undefined;
    onBuffering?.(true);
    v.muted = muted;
    const onReady = () => {
      if (resumeAt.current > 0 && Number.isFinite(v.duration)) {
        try { v.currentTime = Math.min(resumeAt.current, Math.max(0, v.duration - 1)); } catch (_) { /* noop */ }
        resumeAt.current = 0;
      }
      if (wantPlay.current) tryPlay();
    };
    if (source.type === 'hls') {
      detachRef.current = attachHls(v, source.url, {
        onFatal: (k) => advance(k),
        onReady: () => { if (v.readyState >= 1) onReady(); else v.addEventListener('loadedmetadata', onReady, { once: true }); },
        onLevels: (levels, hls) => { hlsRef.current = hls; onQuality?.({ levels, current: -1, auto: true }); },
        onLevel: (lvl, idx) => onQuality?.({ levels: (hlsRef.current?.levels || []).map((l, i) => ({ index: i, height: l.height, bitrate: l.bitrate })), current: idx, auto: hlsRef.current ? hlsRef.current.autoLevelEnabled : true }),
      });
    } else {
      v.src = source.url;
      v.addEventListener('loadedmetadata', onReady, { once: true });
      if (wantPlay.current) tryPlay();
      detachRef.current = () => { v.removeEventListener('loadedmetadata', onReady); v.removeAttribute('src'); v.load(); };
    }
    if (wantPlay.current) armStall();
    return () => { clearStall(); detachRef.current?.(); detachRef.current = null; hlsRef.current = null; onQuality?.(null); };
  }, [source, armed, seen, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only play while on screen and the tab is visible.
  useEffect(() => observeOnScreen(videoRef.current, setOnScreen), [engine]);
  useEffect(() => {
    if (onScreen) setSeen(true);
    const v = videoRef.current;
    if (!v || engine === 'youtube') return;
    if (!onScreen && !v.paused) { autoPaused.current = true; v.pause(); }
    else if (onScreen && autoPaused.current && wantPlay.current) { autoPaused.current = false; tryPlay(); }
  }, [onScreen, engine, tryPlay]);
  // Lazy player on screen but not started: warm just the first two chunks of this film.
  useEffect(() => { if (onScreen && !armed) prefetchFilm(film, { segments: 2 }); }, [onScreen, armed, film]);

  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; ytRef.current?.setMuted(muted); }, [muted]);

  useImperativeHandle(ref, () => ({
    play: ({ unmute = false } = {}) => {
      wantPlay.current = true;
      autoPaused.current = false;
      const v = videoRef.current;
      if (unmute && v) v.muted = false;
      if (engine === 'youtube') { ytRef.current?.play(); return; }
      setArmed(true);
      // Called synchronously inside the user's tap so iOS/Chrome unlock sound on this element.
      if (v) v.play().catch(() => {});
      // Same film already playing: no new 'playing' event will clear the spinner.
      if (v && !v.paused && v.readyState >= 3) onBuffering?.(false);
    },
    pause: () => { wantPlay.current = false; if (engine === 'youtube') ytRef.current?.pause(); else videoRef.current?.pause(); },
    retry: () => { wantPlay.current = true; setArmed(true); setSrcState({ id: film?.id, idx: 0 }); setAttempt((a) => a + 1); },
    seekTo: (s) => { if (engine === 'youtube') ytRef.current?.seekTo(s); else if (videoRef.current) videoRef.current.currentTime = s; },
    setMuted: (m) => { if (videoRef.current) videoRef.current.muted = m; ytRef.current?.setMuted(m); },
    getCurrentTime: () => (engine === 'youtube' ? ytRef.current?.getCurrentTime() || 0 : videoRef.current?.currentTime || 0),
    getDuration: () => (engine === 'youtube' ? ytRef.current?.getDuration() || 0 : videoRef.current?.duration || durationRef.current || 0),
    getVideoElement: () => videoRef.current,
    getEngine: () => engine,
    setQuality: (levelIndex) => {
      const hls = hlsRef.current;
      if (!hls) return;
      if (levelIndex < 0) { hls.currentLevel = -1; hls.nextLevel = -1; hls.loadLevel = -1; }
      else { hls.currentLevel = levelIndex; }
      onQuality?.({ levels: hls.levels.map((l, i) => ({ index: i, height: l.height, bitrate: l.bitrate })), current: levelIndex < 0 ? hls.currentLevel : levelIndex, auto: levelIndex < 0 });
    },
  }), [engine, tryPlay, film?.id]);

  const handleTime = () => {
    const v = videoRef.current; if (!v) return;
    const d = v.duration || 0;
    onTimeUpdate?.(v.currentTime || 0, d);
    if (film?.id && Math.floor(v.currentTime) % 3 === 0) savePosition(film.id, v.currentTime, d);
  };
  const handleEnded = () => { clearPosition(film?.id); onEnded?.(); };

  if (!source) {
    return film?.thumbnail_url
      ? <img src={film.thumbnail_url} alt={film.title || ''} className={`w-full h-full object-${fit} ${className}`} />
      : <div className={`w-full h-full flex items-center justify-center ${className}`}><p className="text-white/30 text-sm tracking-widest uppercase">No video</p></div>;
  }

  if (source.type === 'youtube') {
    return (
      <YouTubePlayer
        ref={ytRef}
        key={`yt-${film.id}`}
        videoId={source.id}
        muted={muted}
        autoplay={wantPlay.current || autoplay}
        onPlay={() => { onBuffering?.(false); onPlay?.(); }}
        onPause={onPause}
        onEnded={handleEnded}
        onCanPlay={() => onBuffering?.(false)}
        onWaiting={() => onBuffering?.(true)}
        onTimeUpdate={(t, d) => { onTimeUpdate?.(t, d); if (d) onDurationChange?.(d); }}
        className={className}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      data-testid="film-player-video"
      data-engine={engine}
      data-armed={armed ? '1' : '0'}
      className={`w-full h-full object-${fit} bg-black ${className}`}
      poster={film.thumbnail_url || undefined}
      preload={!(armed && seen) ? 'none' : engine === 'mp4' ? 'metadata' : 'auto'}
      muted={muted}
      playsInline
      // eslint-disable-next-line react/no-unknown-property
      webkit-playsinline="true"
      x-webkit-airplay="allow"
      controlsList="nodownload noremoteplayback"
      disablePictureInPicture
      onContextMenu={(e) => e.preventDefault()}
      onLoadedMetadata={(e) => { durationRef.current = e.currentTarget.duration; onDurationChange?.(e.currentTarget.duration); }}
      onDurationChange={(e) => { durationRef.current = e.currentTarget.duration; onDurationChange?.(e.currentTarget.duration); }}
      onTimeUpdate={handleTime}
      onWaiting={() => { onBuffering?.(true); armStall(); }}
      onStalled={() => { onBuffering?.(true); armStall(); }}
      onCanPlay={() => { onBuffering?.(false); clearStall(); }}
      onPlaying={() => { onBuffering?.(false); clearStall(); onPlay?.(); }}
      onPlay={() => onPlay?.()}
      onPause={() => { clearStall(); if (!autoPaused.current) onPause?.(); }}
      onEnded={handleEnded}
      onError={() => { if (armed) advance('error'); }}
    />
  );
});

FilmPlayer.displayName = 'FilmPlayer';
export default FilmPlayer;
